import { useCallback, useEffect, useRef, useState } from "react";

export interface PolledItem {
  id: string;
  status: string;
  processingError?: string;
}

export interface PolledStatus {
  status: string;
  processingError?: string;
}

const INITIAL_DELAY_MS = 2500;
const MAX_DELAY_MS = 5000;
const DELAY_STEP_MS = 500;
const TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Polls an admin gallery listing until every tracked ID settles to
 * `"ready"` or `"failed"` — upload `complete` calls only queue background
 * processing (guide §15/§20: "Completion returns 202... poll the admin
 * listing every 2-3 seconds"). Used by both the season album grid and the
 * match gallery grid, which otherwise have unrelated listing shapes.
 */
export function useProcessingPoller() {
  const [statuses, setStatuses] = useState<Record<string, PolledStatus>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppedRef = useRef(true);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stop, [stop]);

  /**
   * Starts (or restarts) polling for the given IDs. `fetchItems` should
   * return every item currently known to the admin listing; only the
   * tracked `pendingIds` are inspected. `onSettle` fires once per ID the
   * moment it reaches `ready` or `failed`, so the caller can patch its own
   * upload-row state without needing to read `statuses` directly.
   */
  const start = useCallback(
    (
      pendingIds: string[],
      fetchItems: () => Promise<PolledItem[]>,
      onSettle?: (id: string, status: string, processingError?: string) => void,
    ) => {
      stop();
      if (pendingIds.length === 0) return;
      stoppedRef.current = false;

      const startedAt = Date.now();
      const remaining = new Set(pendingIds);
      let delay = INITIAL_DELAY_MS;

      const poll = async () => {
        if (stoppedRef.current || remaining.size === 0) return;
        if (Date.now() - startedAt > TIMEOUT_MS) {
          stop();
          return;
        }
        try {
          const items = await fetchItems();
          const byId = new Map(items.map((item) => [item.id, item]));
          remaining.forEach((id) => {
            const item = byId.get(id);
            if (!item) return;
            if (item.status === "ready" || item.status === "failed") {
              setStatuses((prev) => ({
                ...prev,
                [id]: { status: item.status, processingError: item.processingError },
              }));
              onSettle?.(id, item.status, item.processingError);
              remaining.delete(id);
            }
          });
        } catch (e) {
          console.error("Gallery processing poll failed", e);
        }
        if (stoppedRef.current || remaining.size === 0) return;
        delay = Math.min(delay + DELAY_STEP_MS, MAX_DELAY_MS);
        timerRef.current = setTimeout(poll, delay);
      };

      timerRef.current = setTimeout(poll, delay);
    },
    [stop],
  );

  return { statuses, start, stop };
}
